import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowDownToLine, PauseCircle, PlayCircle, Wallet } from "lucide-react";
import { formatDrawCoin } from "../../utils/bondDrawHelpers";
import {
  bondDrawInvestorCashout,
  bondDrawInvestorToggleActive,
  fetchBondDrawInvestors,
  fetchBondDrawPool,
  selectBondDrawInvestorActionLoading,
} from "../../redux/bondDrawSlice";
import BondDrawCashoutModal from "./BondDrawCashoutModal";
import BondPlayConfirmModal from "../bond-play/BondPlayConfirmModal";

const BondDrawInvestorTabActions = ({ investor, onAddBalance, onToast }) => {
  const dispatch = useDispatch();
  const actionLoading = useSelector(selectBondDrawInvestorActionLoading);
  const [showCashout, setShowCashout] = useState(false);
  const [toggleConfirm, setToggleConfirm] = useState(null);

  if (!onAddBalance) return null;

  const isInvestor = !!investor;
  const active = Number(investor?.active || 0);
  const isActive = !!investor?.activeStatus;

  const refresh = () => {
    dispatch(fetchBondDrawPool());
    dispatch(fetchBondDrawInvestors({ limit: 100 }));
  };

  const handleCashout = async (amount) => {
    const result = await dispatch(bondDrawInvestorCashout({ amount }));
    if (bondDrawInvestorCashout.fulfilled.match(result)) {
      setShowCashout(false);
      refresh();
      onToast?.(`Withdrew ${formatDrawCoin(amount)} COIN to your wallet.`);
    } else {
      onToast?.(result.payload || "Cash out failed.");
    }
  };

  const handleToggleActive = async () => {
    const nextActive = toggleConfirm?.nextActive;
    if (typeof nextActive !== "boolean") return;

    const result = await dispatch(bondDrawInvestorToggleActive({ isActive: nextActive }));
    if (bondDrawInvestorToggleActive.fulfilled.match(result)) {
      setToggleConfirm(null);
      refresh();
      onToast?.(nextActive ? "Investor account activated." : "Investor account deactivated.");
    } else {
      onToast?.(result.payload || "Could not update investor status.");
    }
  };

  const openToggleConfirm = () => {
    const nextActive = !isActive;
    setToggleConfirm({
      nextActive,
      title: nextActive ? "Activate investor account?" : "Deactivate investor account?",
      description: nextActive ? (
        <p>Your active balance will be used in new rounds automatically.</p>
      ) : (
        <p>
          Your balance will stop being used for new rounds. Ongoing reservations will complete
          normally.
        </p>
      ),
      confirmLabel: nextActive ? "Activate" : "Deactivate",
      tone: nextActive ? "primary" : "danger",
    });
  };

  return (
    <>
      <div className="bdraw-panel-invest-actions">
        <button type="button" className="bdraw-panel-invest-btn primary" onClick={onAddBalance}>
          <Wallet size={13} strokeWidth={2.5} />
          <span>{isInvestor ? "Add balance" : "Join pool"}</span>
        </button>

        {isInvestor ? (
          <div className="bdraw-panel-invest-row">
            <button
              type="button"
              className="bdraw-panel-invest-btn cashout"
              disabled={actionLoading || active <= 0}
              onClick={() => setShowCashout(true)}
            >
              <ArrowDownToLine size={13} strokeWidth={2.5} />
              <span>Cash out</span>
              {active > 0 ? <em>{formatDrawCoin(active)}</em> : null}
            </button>
            <button
              type="button"
              className={`bdraw-panel-invest-btn ${isActive ? "deactivate" : "activate"}`}
              disabled={actionLoading}
              onClick={openToggleConfirm}
            >
              {isActive ? (
                <PauseCircle size={13} strokeWidth={2.5} />
              ) : (
                <PlayCircle size={13} strokeWidth={2.5} />
              )}
              <span>{isActive ? "Deactivate" : "Activate"}</span>
            </button>
          </div>
        ) : null}
      </div>

      {isInvestor ? (
        <BondDrawCashoutModal
          open={showCashout}
          maxAmount={active}
          loading={actionLoading}
          onClose={() => !actionLoading && setShowCashout(false)}
          onSubmit={handleCashout}
        />
      ) : null}

      <BondPlayConfirmModal
        open={!!toggleConfirm}
        eyebrow="Investor account"
        title={toggleConfirm?.title}
        description={toggleConfirm?.description}
        confirmLabel={toggleConfirm?.confirmLabel || "Confirm"}
        tone={toggleConfirm?.tone || "primary"}
        loading={actionLoading}
        onClose={() => !actionLoading && setToggleConfirm(null)}
        onConfirm={handleToggleActive}
      />
    </>
  );
};

export default BondDrawInvestorTabActions;
